import { addMinutes, getDay } from "date-fns";
import { useCallback, useMemo } from "react";
import { useFormContext } from "react-hook-form";

export type MinimalConfig = {
  name: string;
  durationInMinutes: number;
};

export type UsePlanningChainArgs = {
  /** Full ordered config list for the current screen (FCR-N or FCR-D) */
  allConfigs: MinimalConfig[];
  /** Current item’s config */
  config: MinimalConfig;
  /** Index of current item in `allConfigs` */
  index: number;
  /** Initial start date for the whole flow (usually `initialData.startDate`) */
  initialStartDate: Date;
};

export function usePlanningChain({ allConfigs, config, index, initialStartDate }: UsePlanningChainArgs) {
  const { setValue, getValues } = useFormContext();

  // Current planning value from form
  const currentPlanning: any = getValues(config.name);
  const currentActive = !!currentPlanning?.active;

  // Find previous ACTIVE planning values (walk backwards)
  const getPreviousActivePlanning = useCallback(() => {
    let prevIndex = index - 1;
    while (prevIndex >= 0) {
      const prevCfg = allConfigs[prevIndex];
      const prevActive = getValues(`${prevCfg.name}.active`);
      if (prevActive) return getValues(prevCfg.name);
      prevIndex--;
    }
    return null;
  }, [index, allConfigs, getValues]);

  const prevPlanningValues = getPreviousActivePlanning();

  // UI helpers
  const isOverlappingDays = useMemo(() => {
    const sd = currentPlanning?.start_date;
    const ed = currentPlanning?.end_date;
    if (!sd || !ed) return false;
    return getDay(sd) !== getDay(ed);
  }, [currentPlanning]);

  const minDateTime = useMemo(
    () => (prevPlanningValues ? new Date(prevPlanningValues.end_date) : new Date()),
    [prevPlanningValues],
  );

  const validateStartDate = useCallback(
    (value: Date | null) => {
      if (!value) return "Start date is required";
      const minAllowed = prevPlanningValues ? new Date(prevPlanningValues.end_date) : new Date();
      if (value < minAllowed) {
        return prevPlanningValues
          ? "Start date cannot be before the previous planning's end date"
          : "Start date cannot be in the past";
      }
      return undefined;
    },
    [prevPlanningValues],
  );

  // Recalculate the chain starting from changedIndex + 1
  const recalcFollowingPlannings = useCallback(
    (changedIndex: number, newStart: Date) => {
      let currentStart = newStart;
      allConfigs.slice(changedIndex + 1).forEach((nextCfg) => {
        const active = getValues(`${nextCfg.name}.active`);
        if (active) {
          setValue(`${nextCfg.name}.start_date`, currentStart, { shouldDirty: true });
          const newEnd = addMinutes(currentStart, nextCfg.durationInMinutes);
          setValue(`${nextCfg.name}.end_date`, newEnd, { shouldDirty: true });
          currentStart = newEnd;
        }
      });
    },
    [allConfigs, getValues, setValue],
  );

  // Handlers
  const handleStartDateChange = useCallback(
    (newDate: Date | null) => {
      if (!newDate) return;
      setValue(`${config.name}.start_date`, newDate, { shouldDirty: true });
      const newEnd = addMinutes(newDate, config.durationInMinutes);
      setValue(`${config.name}.end_date`, newEnd, { shouldDirty: true });
      recalcFollowingPlannings(index, newEnd);
    },
    [config.name, config.durationInMinutes, index, recalcFollowingPlannings, setValue],
  );

  const handleEndDateChange = useCallback(
    (newDate: Date | null) => {
      if (!newDate) return;
      setValue(`${config.name}.end_date`, newDate, { shouldDirty: true });
      recalcFollowingPlannings(index, newDate);
    },
    [config.name, index, recalcFollowingPlannings, setValue],
  );

  const handleActiveToggle = useCallback(
    (newActive: boolean) => {
      setValue(`${config.name}.active`, newActive, { shouldDirty: true });
      if (newActive) {
        const newStart: Date = prevPlanningValues ? prevPlanningValues.end_date : initialStartDate;
        setValue(`${config.name}.start_date`, newStart, { shouldDirty: true });
        const newEnd = addMinutes(newStart, config.durationInMinutes);
        setValue(`${config.name}.end_date`, newEnd, { shouldDirty: true });
        recalcFollowingPlannings(index, newEnd);
      } else {
        const recalcStart: Date = prevPlanningValues
          ? new Date(prevPlanningValues.end_date)
          : new Date(initialStartDate);
        recalcFollowingPlannings(index - 1, recalcStart);
      }
    },
    [
      config.name,
      config.durationInMinutes,
      index,
      prevPlanningValues,
      recalcFollowingPlannings,
      setValue,
      initialStartDate,
    ],
  );

  return {
    currentPlanning,
    currentActive,
    isOverlappingDays,
    prevPlanningValues,
    minDateTime,
    validateStartDate,
    handleStartDateChange,
    handleEndDateChange,
    handleActiveToggle,
  };
}
